import request from 'shared/lib/request';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { showMessage } from 'shared/feedback/messages';
import { i18n } from 'shared/i18n';
import { labelKeys } from './keys';

export function useLabels(params: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: labelKeys.list(),

    queryFn: async () => {
      const urlParams = Object.entries(params)
        .filter(([, value]) => !!value)
        .map(([key, value]) => {
          const searchValue = encodeURIComponent(`${value}${key === 'key' || key === 'text' ? '%' : ''}`);
          return `${key}=${searchValue}`;
        });

      const res = await request.get(`/foundation/labels?size=20&${urlParams}`).catch((err) => {
        if (err.status === 404) {
          return null;
        } else {
          throw err;
        }
      });

      return { items: res?.data?._embedded?.labels || [], page: res?.data?.page };
    },
  });
}

export function useLabelsByKey(key: string | undefined) {
  return useQuery({
    queryKey: labelKeys.byKey(key),

    queryFn: async () => {
      const res = await request.get(`/foundation/labels?key=${key}`).catch((err) => {
        if (err.status === 404) {
          return null;
        } else {
          throw err;
        }
      });

      return res?.data?._embedded?.labels || [];
    },

    enabled: false,
  });
}

export function useCreateLabel() {
  return useMutation({
    mutationFn: async ({ texts, key, visibility }: { texts: Record<string, string>; key: string; visibility?: string }) => {
      return await Promise.all(
        Object.entries(texts).map(([locale, text]) => {
          return request.post(`/foundation/labels`, { text, key, visibility, locale });
        })
      );
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      showMessage('success', i18n('label:created'));
    },
  });
}

export function useUpdateLabel() {
  return useMutation({
    mutationFn: async (labels: Array<{ id: number } & Record<string, unknown>>) => {
      return await Promise.all(
        labels.map((label) => {
          return request.put(`/foundation/labels/${label.id}`, label);
        })
      );
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      showMessage('success', i18n('label:updated'));
    },
  });
}

export function useAllLabels(locale: string = '') {
  return useQuery({
    queryKey: labelKeys.allLabels(locale),

    queryFn: async () => {
      const pageSize = 5000;
      const localeParam = locale ? `&locale=${locale}` : '';
      const fetchPage = (page: number) =>
        request.get(`/foundation/labels?size=${pageSize}&page=${page}${localeParam}`).catch((err) => {
          if (err.status === 404) {
            return null;
          } else {
            throw err;
          }
        });

      const first = await fetchPage(0);
      if (!first) return [];

      const totalPages = first.data?.page?.totalPages ?? 1;
      const labels = first.data?._embedded?.labels || [];

      if (totalPages <= 1) return labels;

      const rest = await Promise.all(Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 1)));
      rest.forEach((res) => {
        if (res?.data?._embedded?.labels) labels.push(...res.data._embedded.labels);
      });

      return labels;
    },
  });
}

export function useImportLabels() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ content, mode }: { content?: { labels?: unknown[] }; mode: string }) => {
      return request.put(`/foundation/labels/list?mode=${mode}`, content?.labels);
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: labelKeys.list() });
    },
  });
}
